import React, { FC, useState, useRef, useEffect } from 'react';
import Departments from 'models/departments';
import { useAppContext } from '../../store/appContext';
import ReactPixel from '../../helpers/pixels/fbPixel';
import { CONTEXT_SEARCH } from '../../helpers/tracking/stringValues';
import { search } from '../../helpers/routes';
import DepartmentBox from '../DepartmentBox/DepartmentBox';
import SearchDepartment from './SearchDepartment';
import { SearchInput } from './styled';
import { ClearButton } from './styled/ClearButton';
import { useUrlBuilder } from '../../hooks/useUrlBuilder';
import { departmentSelectedEvent, productsSearchedEvent } from '../../common/models/TrackingEvents';
import { track } from '../../helpers/tracking/tracking';

/* Types */
interface SearchBoxProps {
  hideSearchBox?: () => void;
  menuDepartment?: string;
  mobileSubmit?: boolean;
}

const SearchBox: FC<SearchBoxProps> = ({ hideSearchBox, menuDepartment, mobileSubmit }) => {
  const { department: departmentContext } = useAppContext();
  const searchInputRef = useRef<HTMLDivElement>();
  const searchBoxRef = useRef<HTMLDivElement>();
  const { localized, withCDN } = useUrlBuilder();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchDepartment, setSearchDepartment] = useState(Departments.getInternalName(departmentContext));
  const [showDepartments, setShowDepartments] = useState(false);

  const submitSearch = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    const term = searchQuery.trim();
    if (!term) {
      return;
    }

    /* eslint-disable @typescript-eslint/camelcase */
    ReactPixel.track('Search', { search_string: searchQuery });
    track(productsSearchedEvent, {
      categoryDepartment: { department: searchDepartment.toLowerCase() },
      ui: { term },
    });
    window.location = (localized(search(searchDepartment, term)) as undefined) as Location;
  };

  const handleFocusOutIfMobile = e => {
    const { relatedTarget } = e;

    if (!mobileSubmit || !searchBoxRef) {
      return null;
    }

    const { current: searchBox } = searchBoxRef;
    if (!relatedTarget || searchBox === relatedTarget || !searchBox.contains(relatedTarget)) {
      submitSearch();
      return null;
    }

    return null;
  };

  const handleClickOutside = event => {
    if (!searchBoxRef?.current?.contains(event.target)) {
      setShowDepartments(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    searchBoxRef.current.addEventListener('focusout', handleFocusOutIfMobile);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      searchBoxRef?.current.removeEventListener('focusout', handleFocusOutIfMobile);
    };
  }, []);

  useEffect(() => {
    if (!showDepartments) {
      searchInputRef.current.blur();
    }
  }, [showDepartments]);

  useEffect(() => {
    if (menuDepartment && menuDepartment !== searchDepartment) setSearchDepartment(menuDepartment);
  }, [menuDepartment]);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handlerDepartment = (department, e) => {
    e.stopPropagation();
    setSearchDepartment(department);
    const searchDep = Departments.getInternalName(department);
    track(departmentSelectedEvent, {
      categoryDepartment: {
        department: searchDep.toLowerCase(),
      },
      ui: { context: CONTEXT_SEARCH },
    });
    searchInputRef.current.focus();
  };

  return (
    <>
      <div
        ref={searchBoxRef}
        className="search-box-wrapper"
        onClick={() => {
          if (mobileSubmit) {
            hideSearchBox();
          }
        }}
        style={{ display: 'flex' }}
      >
        <form action="#" id="search" onSubmit={submitSearch} tabIndex={0}>
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative' }} tabIndex={0}>
            <input
              alt="Submit search"
              className="search-box-icon"
              data-cy="image-search-submit"
              id="Search"
              onClick={e => {
                e.stopPropagation();
                submitSearch(e);
              }}
              src={withCDN('mkt/header/magnifying_glass.svg')}
              type="image"
            />

            <SearchInput
              ref={searchInputRef}
              autoComplete="off"
              data-cy="input-search-free-text"
              onChange={handleSearchInput}
              onClick={e => e.stopPropagation()}
              onFocus={() => setShowDepartments(true)}
              placeholder="Search"
              tabIndex="0"
              type="search"
              value={searchQuery}
            />

            {searchQuery && (
              <ClearButton
                onClick={() => {
                  setSearchQuery('');
                }}
              />
            )}

            <SearchDepartment showDepartments={showDepartments}>
              <DepartmentBox
                context="search"
                department={searchDepartment}
                fontSize="13px"
                handleClick={handlerDepartment}
                underline
              />
            </SearchDepartment>
          </div>
        </form>
      </div>
      <style jsx>
        {`
          .search-box-wrapper {
            display: flex;
            position: relative;
            top: -10px;
          }

          .search-box-icon {
            outline: none;
            position: absolute;
            width: 23px;
          }

          @media (max-width: 1023px) {
            .search-box-wrapper {
              background-color: #ffffff;
              min-height: 99%;
              position: fixed;
              top: 50px;
              width: 100%;
              z-index: 10;
              justify-content: center;
            }

            .search-box-icon {
              left: 5px;
              top: 45%;
            }
          }

          @media (min-width: 1023px) {
            .search-box-icon {
              top: 40%;
              transform: translateY(-50%);
              left: 12px;
            }
          }
        `}
      </style>
    </>
  );
};

export default SearchBox;
